import { apiClient } from '@/lib/api-client';

/** The class filter as the expenses file takes it: one class, or `none` for «Nessuna». */
export type ExportClass = 'A1' | 'A2' | 'P' | 'PG' | 'PD' | 'none';

/** `owner` is the contract's repeatable user id. */
export type NotesExportQuery = { readonly year?: number; readonly owner?: string[] };
export type ExpensesExportQuery = {
  readonly class?: ExportClass;
  readonly from?: string;
  readonly to?: string;
  readonly owner?: string[];
};

/** What a download ended in: the file was saved, or the server's `code` (empty when unknown). */
export type ExportOutcome = { readonly ok: true } | { readonly ok: false; readonly code: string };

type Raw = Promise<{ data?: unknown; error?: unknown; response: Response }>;

/** The server's own name, from `Content-Disposition`; the fallback when it sends none. */
export function fileNameOf(response: Response, fallback: string): string {
  const header = response.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? fallback;
}

/** Through the typed client (bearer token, X-Active-* headers), then saved under its name. */
async function save(run: () => Raw, fallback: string): Promise<ExportOutcome> {
  try {
    const { data, error, response } = await run();
    if (!response.ok || !(data instanceof Blob)) {
      const code =
        typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
      return { ok: false, code };
    }
    const url = URL.createObjectURL(data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileNameOf(response, fallback);
    anchor.click();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch {
    return { ok: false, code: '' };
  }
}

/** «Esporta note spese» (#915 F4, design §6.1): every note of the year, or of all years. */
export function downloadNotesExport(query: NotesExportQuery): Promise<ExportOutcome> {
  return save(
    () =>
      apiClient.GET('/v1/expenses/exports/notes.xlsx', {
        params: { query },
        parseAs: 'blob',
      }),
    'note-spese.xlsx',
  );
}

/** «Esporta spese per classe» (#915 F4, design §6.2). */
export function downloadExpensesExport(query: ExpensesExportQuery): Promise<ExportOutcome> {
  return save(
    () =>
      apiClient.GET('/v1/expenses/exports/expenses.xlsx', {
        params: { query },
        parseAs: 'blob',
      }),
    'spese.xlsx',
  );
}
